"use client";

import { useState, type FC } from "react";
import Image from "next/image";
import {
  BlockWrapper,
  H2,
  MarkdownRenderer,
  SectionTag,
} from "@/shared/components";
import { LinkArrowIcon } from "@/shared/components/LinkArrowIcon";
import { linkButtonVariants } from "@/shared/components/LinkButton";
import type { BlockSectionBg } from "./common/types";
import {
  getBodyTextClass,
  getMarkdownTextOverrideClass,
  getSectionBorderClass,
  getSectionHeaderMobilePaddingClass,
  getSectionTaglineSidebarClass,
  sectionBgToWrapper,
} from "./blockSection";
import { resolveClientsTheme } from "./clientsTheme";
import { cn } from "@/lib/utils";
import type { ImageDataType } from "@/shared/types";

export type ClientItemType = {
  title: string;
  image: ImageDataType | null;
};

interface ClientsBlockProps {
  headline?: string;
  tagline?: string;
  description?: string;
  clients?: ClientItemType[];
  sectionBg?: BlockSectionBg;
}

const VISIBLE_MOBILE = 4;
const VISIBLE_TABLET = 8;
const VISIBLE_DESKTOP = 12;

const MOBILE_COLUMNS = 2;
const TABLET_COLUMNS = 4;
const DESKTOP_COLUMNS = 6;

const descriptionProseClass =
  "font-sans text-base leading-[1.2] [&_a]:text-link [&_li]:mt-0 [&_ol]:list-decimal [&_ol]:pl-6 [&_p+p]:mt-0 [&_ul]:list-disc [&_ul]:pl-6";

function getLogoGridColumnsClass(columns: number): string {
  if (columns === 2) return "grid-cols-2";
  if (columns === 4) return "grid-cols-4";
  return "grid-cols-6";
}

function ClientLogoCell({
  client,
  index,
  logoImageClass,
  logoHeightClass,
}: {
  client: ClientItemType;
  index: number;
  logoImageClass: string;
  logoHeightClass: string;
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 items-center justify-center px-2",
        logoHeightClass
      )}
    >
      {client.image ? (
        <Image
          className={cn(
            "h-full w-full max-h-full max-w-full object-contain transition-all duration-300",
            logoImageClass
          )}
          src={client.image.src}
          alt={
            client.image.alt ||
            client.title ||
            `Логотип клиента ${index + 1}`
          }
          title={client.image.title ?? undefined}
          width={240}
          height={100}
          loading="lazy"
          unoptimized={client.image.isSvg === true}
        />
      ) : (
        <span className="text-center text-sm font-semibold leading-tight text-muted-foreground">
          {client.title}
        </span>
      )}
    </div>
  );
}

function ClientLogoGrid({
  clients,
  columns,
  gapClass,
  logoImageClass,
  logoHeightClass,
}: {
  clients: ClientItemType[];
  columns: number;
  gapClass: string;
  logoImageClass: string;
  logoHeightClass: string;
}) {
  return (
    <div
      className={cn(
        "grid w-full min-w-0",
        getLogoGridColumnsClass(columns),
        gapClass
      )}
    >
      {clients.map((client, index) => (
        <ClientLogoCell
          key={`${client.title}-${index}`}
          client={client}
          index={index}
          logoImageClass={logoImageClass}
          logoHeightClass={logoHeightClass}
        />
      ))}
    </div>
  );
}

function ClientsToggleButton({
  showAll,
  onToggle,
  className,
}: {
  showAll: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={cn(
        linkButtonVariants({ variant: "linkArrow", size: "link" }),
        "gap-2",
        className
      )}
      onClick={onToggle}
    >
      <span className="underline decoration-solid decoration-current underline-offset-4">
        {showAll ? "скрыть" : "смотреть все"}
      </span>
      <LinkArrowIcon />
    </button>
  );
}

export const ClientsBlock: FC<ClientsBlockProps> = ({
  headline,
  tagline,
  description,
  clients = [],
  sectionBg = "light",
}) => {
  const [showAll, setShowAll] = useState(false);
  const theme = resolveClientsTheme(sectionBg);
  const borderClass = getSectionBorderClass(sectionBg);
  const bodyTextClass = cn(
    getBodyTextClass(sectionBg),
    theme.bodyText,
    getMarkdownTextOverrideClass(sectionBg)
  );

  const hasHeader = Boolean(headline || tagline || description);

  const hasMoreMobile = clients.length > VISIBLE_MOBILE;
  const hasMoreTablet = clients.length > VISIBLE_TABLET;
  const hasMoreDesktop = clients.length > VISIBLE_DESKTOP;

  const visibleMobile = showAll ? clients : clients.slice(0, VISIBLE_MOBILE);
  const visibleTablet = showAll ? clients : clients.slice(0, VISIBLE_TABLET);
  const visibleDesktop = showAll ? clients : clients.slice(0, VISIBLE_DESKTOP);

  const logoHeightMobile = "h-[60px]";
  const logoHeightTablet = "h-[60px]";
  const logoHeightDesktop = "h-[100px]";

  const handleToggle = () => setShowAll((value) => !value);

  return (
    <BlockWrapper bgColor={sectionBgToWrapper(sectionBg)} noPadding>
      <div className="w-full min-w-0">
        {hasHeader && (
          <div
            className={cn(
              getSectionHeaderMobilePaddingClass(),
              "flex flex-col gap-4 border-b",
              "desktop:flex-row desktop:gap-0 desktop:border-b desktop:p-0",
              borderClass
            )}
          >
            {tagline && (
              <div
                className={cn(
                  getSectionTaglineSidebarClass(),
                  "desktop:w-1/3 desktop:border-r",
                  borderClass
                )}
              >
                <SectionTag
                  label={tagline}
                  variant={theme.tagVariant}
                  className="uppercase"
                />
              </div>
            )}

            <div
              className={cn(
                "flex flex-col gap-4",
                "desktop:flex-1 desktop:px-10 desktop:py-20"
              )}
            >
              {headline && (
                <H2
                  className={cn(
                    "text-heading-2-sm md:text-heading-2",
                    theme.headline
                  )}
                >
                  {headline}
                </H2>
              )}
              {description && (
                <MarkdownRenderer
                  content={description}
                  className={cn(descriptionProseClass, bodyTextClass)}
                />
              )}
            </div>
          </div>
        )}

        <div
          className={cn(
            "flex flex-col items-center gap-12 px-4 py-10",
            "md:px-6 md:pb-12 md:pt-10",
            "desktop:gap-12 desktop:px-10 desktop:py-20",
            borderClass
          )}
        >
          <div className="w-full min-w-0">
            <div className="flex flex-col items-center gap-12 md:hidden">
              <ClientLogoGrid
                clients={visibleMobile}
                columns={MOBILE_COLUMNS}
                gapClass="gap-x-4 gap-y-12"
                logoImageClass={theme.logoImageClass}
                logoHeightClass={logoHeightMobile}
              />
              {hasMoreMobile && (
                <ClientsToggleButton
                  showAll={showAll}
                  onToggle={handleToggle}
                  className={theme.toggleLink}
                />
              )}
            </div>

            <div className="hidden flex-col items-center gap-12 md:flex desktop:hidden">
              <ClientLogoGrid
                clients={visibleTablet}
                columns={TABLET_COLUMNS}
                gapClass="gap-x-4 gap-y-10"
                logoImageClass={theme.logoImageClass}
                logoHeightClass={logoHeightTablet}
              />
              {hasMoreTablet && (
                <ClientsToggleButton
                  showAll={showAll}
                  onToggle={handleToggle}
                  className={theme.toggleLink}
                />
              )}
            </div>

            <div className="hidden flex-col items-center gap-12 desktop:flex">
              <ClientLogoGrid
                clients={visibleDesktop}
                columns={DESKTOP_COLUMNS}
                gapClass="gap-x-6 gap-y-12"
                logoImageClass={theme.logoImageClass}
                logoHeightClass={logoHeightDesktop}
              />
              {hasMoreDesktop && (
                <ClientsToggleButton
                  showAll={showAll}
                  onToggle={handleToggle}
                  className={theme.toggleLink}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </BlockWrapper>
  );
};
